import React, { useMemo, useRef, useState } from 'react'
import type { Column } from 'react-table'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import { Icon } from '~/components/atoms/Icon/Icon'
import PortfolioTableArtwork from '~/components/atoms/PortfolioTableArtwork/PortfolioTableArtwork'
import Table from '~/components/atoms/Table/Table'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import useGeoLocation from '~/hooks/useLocation'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { currencyCodeToSymbolMap } from '~/utils/currencyMappers'
import { numberWithCommas } from '~/utils/formatCurrency'

import GrowthStatus from '../GrowthStatus/GrowthStatus'
import type { MAB100CollectionCardItemProps } from '../MAB100CollectionCard/MAB100CollectionCard'
import MAB100CollectionCard from '../MAB100CollectionCard/MAB100CollectionCard'

import useHooks from './hooks'

import styles from './MAB100CollectionTable.module.css'

export interface MAB100CollectionTableProps {
  testID?: string
  items: MAB100CollectionCardItemProps[]
}

interface TableViewProps {
  items: MAB100CollectionCardItemProps[]
  openForm: (
    supplyProps: FormTypes,
    item: MAB100CollectionCardItemProps
  ) => void
}

type Listing = {
  rank?: number
  artworkDetails: React.ReactElement
  guidePrice?: JSX.Element
  growth5Year?: JSX.Element
  tradingStatus?: JSX.Element
}

const TableView = ({ items, openForm }: TableViewProps) => {
  const t = useTranslations('mab100.collectionTable')
  const { activeCurrencyIso } = useGeoLocation()

  const currentCurrency = activeCurrencyIso
    ? currencyCodeToSymbolMap[activeCurrencyIso]
    : '£'

  const growth: Column<Listing> = {
    Header: (
      <Text uppercase size={TextSizes.bodySml}>
        <span dangerouslySetInnerHTML={{ __html: t('headers.growthAagr') }} />
      </Text>
    ),
    minWidth: 100,
    accessor: 'growth5Year',
  }

  const columns: Column<Listing>[] = [
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('headers.rank')}
        </Text>
      ),
      minWidth: 100,
      maxWidth: 100,
      accessor: 'rank',
    },
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          <span dangerouslySetInnerHTML={{ __html: t('headers.artwork') }} />
        </Text>
      ),
      minWidth: 400,
      maxWidth: 400,
      // width: 'auto',
      accessor: 'artworkDetails',
    },
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('headers.estimatedValue')}
        </Text>
      ),
      // width: 'auto',
      maxWidth: 230,
      minWidth: 230,
      accessor: 'guidePrice',
    },
    growth,
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          <span
            dangerouslySetInnerHTML={{ __html: t('headers.tradingStatus') }}
          />
        </Text>
      ),
      maxWidth: 10,
      minWidth: 10,
      accessor: 'tradingStatus',
    },
  ]

  const renderAmount = (amount: number) => {
    return (
      <>
        {currentCurrency}
        {numberWithCommas(amount)}
      </>
    )
  }

  const renderTradingStatus = (item: MAB100CollectionCardItemProps) => {
    const buyable = item.sellTicketsCount > 1

    const sellable = item.buyTicketsCount > 0

    const sellStatus = () => (
      <div className={styles.cellTradingStatus}>
        <Text uppercase>{t('tradingStatus.wanted')}</Text>
        <Button
          variant={ButtonVariants.primaryDark}
          reducedPadding={true}
          smallFont={true}
          iconLeft={<Icon type={'Arrow'} />}
          button={{
            type: ButtonTypes.button,
            onClick: () => openForm(FormTypes.sell, item),
          }}
        >
          {t('buttons.enquireToSell')}
        </Button>
      </div>
    )

    if (sellable) sellStatus()

    if (buyable) {
      return (
        <div className={styles.cellTradingStatus}>
          <Text uppercase>{t('tradingStatus.inNetwork')}</Text>
          <Button
            variant={ButtonVariants.primaryDark}
            reducedPadding={true}
            smallFont={true}
            iconLeft={<Icon type={'Arrow'} />}
            button={{
              type: ButtonTypes.button,
              onClick: () => openForm(FormTypes.buy, item),
            }}
          >
            {t('buttons.enquireToBuy')}
          </Button>
        </div>
      )
    }

    return sellStatus()
  }

  const data =
    items &&
    items.map(item => {
      return {
        rank: (
          <div className={classNames(styles.cell, styles.cellRank)}>
            {item.rank}
          </div>
        ),
        artworkDetails: <PortfolioTableArtwork item={item} linkArtwork />,
        guidePrice: item.listPriceMin ? (
          <div className={styles.cell}>
            {renderAmount(item.listPriceMin)}&nbsp;-&nbsp;
            {renderAmount(item.listPriceMax)}
          </div>
        ) : (
          ''
        ),
        growth5Year: item?.aagr ? (
          <GrowthStatus size="medium" graphGrey growth={+item.aagr} />
        ) : (
          ''
        ),
        tradingStatus: renderTradingStatus(item),
      }
    })

  return (
    <Table<Listing>
      name="mab100-collection"
      columns={columns}
      noMarginTop
      data={data as any}
      minHeight={200}
    />
  )
}

type Paginate = 1 | 2 | 3 | 4

export default function MAB100CollectionTable({
  testID,
  items,
}: MAB100CollectionTableProps) {
  const t = useTranslations('mab100.collectionTable')
  const { openForm } = useHooks()
  const [paginate, setPaginate] = useState<Paginate | number>(1)
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)
  const artworksElRef = useRef<HTMLDivElement>(null)

  const paginationHandler = (num: number) => {
    if (!artworksElRef.current) return
    const offset =
      artworksElRef.current.getBoundingClientRect().top +
      document.documentElement.scrollTop -
      25
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    })
    setPaginate(num)
  }

  const pagination = () => {
    return (
      <div className={styles.paginationContainer}>
        <div
          className={classNames(
            styles.paginationItem,
            paginate === 1 && styles.active
          )}
          onClick={() => {
            paginationHandler(1)
          }}
        >
          <Text size={TextSizes.primaryTitleMed}>1-24</Text>
        </div>
        <div
          className={classNames(
            styles.paginationItem,
            paginate === 2 && styles.active
          )}
          onClick={() => {
            paginationHandler(2)
          }}
        >
          <Text size={TextSizes.primaryTitleMed}>25-49</Text>
        </div>
        <div
          className={classNames(
            styles.paginationItem,
            paginate === 3 && styles.active
          )}
          onClick={() => {
            paginationHandler(3)
          }}
        >
          <Text size={TextSizes.primaryTitleMed}>50-74</Text>
        </div>
        <div
          className={classNames(
            styles.paginationItem,
            paginate === 4 && styles.active
          )}
          onClick={() => {
            paginationHandler(4)
          }}
        >
          <Text size={TextSizes.primaryTitleMed}>75-100</Text>
        </div>
      </div>
    )
  }

  const paginatedData = useMemo(() => {
    const num = 24
    const from = num * (paginate - 1)
    const to = num * paginate

    // stop the page from breaking if no items are passed
    const cItems = items && items?.length > 0 ? [...items] : []

    return cItems.splice(from, to)
  }, [paginate])

  const renderMobileView = () => {
    return (
      <div ref={artworksElRef}>
        <Container>{pagination()}</Container>

        {paginatedData.map((item, i) => {
          return (
            <div key={`mab100-item-${i}`} className={styles.portfolioCardWrap}>
              <MAB100CollectionCard
                item={item}
                rank={item.rank}
                openForm={openForm}
              />
            </div>
          )
        })}

        <Container>
          <div className={classNames(styles.paginationSmall)}>
            <Text
              size={TextSizes.primaryTitleMed}
              className={styles.paginationTitle}
            >
              {t('viewMore')}
            </Text>
            {pagination()}
          </div>
        </Container>
      </div>
    )
  }

  if (isMobile) return <>{renderMobileView()}</>

  return (
    <div
      className={classNames(styles.base)}
      data-testid={testID}
      ref={artworksElRef}
    >
      <Container>
        {pagination()}
        <TableView items={paginatedData} openForm={openForm} />
        <div className={styles.paginationSmall}>
          <Text
            size={TextSizes.primaryTitleMed}
            className={styles.paginationTitle}
          >
            {t('viewMore')}
          </Text>
          {pagination()}
        </div>
      </Container>
    </div>
  )
}
